import type { SnapshotFile } from "../../../schemas/filesystem";

export interface FileTreeNode {
  name: string;
  path: string;
  type: "file" | "folder";
  size?: string;
  modified?: string;
  content?: string;
  children?: FileTreeNode[];
}

/**
 * Convert a flat array of SnapshotFiles into a hierarchical tree structure
 */
export function buildFileTree(
  files: SnapshotFile[],
  // timestamp?: string,
): FileTreeNode[] {
  const tree: FileTreeNode[] = [];
  const pathMap = new Map<string, FileTreeNode>();

  // Sort files to ensure consistent ordering
  const sortedFiles = [...files].sort((a, b) => a.path.localeCompare(b.path));

  for (const file of sortedFiles) {
    const pathParts = file.path.split("/");
    let currentPath = "";

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      const isLastPart = i === pathParts.length - 1;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!pathMap.has(currentPath)) {
        const node: FileTreeNode = {
          name: part,
          path: currentPath,
          type: isLastPart ? "file" : "folder",
          children: isLastPart ? undefined : [],
        };

        if (isLastPart) {
          // Add file-specific properties
          node.content = file.content;
          node.size = formatFileSize(file.content.length);
          node.modified =
            file.updatedAt instanceof Date
              ? formatRelativeTime(file.updatedAt.toISOString())
              : "Unknown";
        }

        pathMap.set(currentPath, node);

        // Add to appropriate parent
        if (i === 0) {
          // Root level
          tree.push(node);
        } else {
          const parentPath = pathParts.slice(0, i).join("/");
          const parent = pathMap.get(parentPath);
          if (parent?.children) {
            parent.children.push(node);
          }
        }
      }
    }
  }

  // Sort each level: folders first, then files, both alphabetically
  function sortTreeLevel(nodes: FileTreeNode[]): FileTreeNode[] {
    return nodes
      .sort((a, b) => {
        // Folders first, then files
        if (a.type !== b.type) {
          return a.type === "folder" ? -1 : 1;
        }
        // Within same type, sort alphabetically
        return a.name.localeCompare(b.name);
      })
      .map((node) => ({
        ...node,
        children: node.children ? sortTreeLevel(node.children) : undefined,
      }));
  }

  return sortTreeLevel(tree);
}

/**
 * Format file size in bytes to human readable string
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) {
    return "0 B";
  }

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format a timestamp as relative time (e.g., "2 hours ago", "3 days ago")
 */
function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Convert to different time units
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffMinutes < 1) {
    return "just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }
  if (diffWeeks < 4) {
    return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
  }
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths === 1 ? "" : "s"} ago`;
  }
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears === 1 ? "" : "s"} ago`;
}

/**
 * Get file statistics from a file tree
 */
export function getFileTreeStats(tree: FileTreeNode[]): {
  totalFiles: number;
  totalFolders: number;
  totalSize: number;
} {
  let totalFiles = 0;
  let totalFolders = 0;
  let totalSize = 0;

  function traverse(nodes: FileTreeNode[]) {
    for (const node of nodes) {
      if (node.type === "file") {
        totalFiles++;
        totalSize += node.content ? node.content.length : 0;
      } else {
        totalFolders++;
        if (node.children) {
          traverse(node.children);
        }
      }
    }
  }

  traverse(tree);

  return { totalFiles, totalFolders, totalSize };
}

/**
 * Find a file node by path in the tree
 */
export function findFileInTree(
  tree: FileTreeNode[],
  path: string,
): FileTreeNode | null {
  for (const node of tree) {
    if (node.path === path) {
      return node;
    }
    if (node.children) {
      const found = findFileInTree(node.children, path);
      if (found) {
        return found;
      }
    }
  }
  return null;
}
